// React
import { useContext, useEffect, useState } from "react"

// Components
import { ActionButton, ImportList } from "../../components"

// Contexts
import { DataContext, MasterContext } from "../contexts"

// Form
import { ValidationError } from "yup"

// Types
import type { ErrorFetch, SuccessFetch } from "types"
import type { DataType } from "../../types"

// Utils
import { useApi } from "utils"
import { getFinalMessage, useRowStatus } from "../../utils"
import { fetchMaster, masterDataKeyList, productValidationSchema } from "../utils"

export function ImportData(): JSX.Element {
  // Hooks
  const { data } = useContext(DataContext)
  const { error, index, success, toggle } = useRowStatus()

  useEffect(() => {
    if (index >= data.length) {
      getFinalMessage(data, error, success)
    }

    // eslint-disable-next-line
  }, [index])

  return (
    <ActionButton
      data={data}
      error={error}
      index={index}
      onStartImport={toggle}
    >
      {[...Array(data.length)].map((_, key) => {
        // Vars
        const item = data[key]

        return (
          <ImportRow
            key={key}
            activeIndex={index}
            data={item}
            error={error.find((item) => key === item.index)?.message}
            index={key}
            onError={(value) => toggle("error", value)}
            onSuccess={() => toggle("success")}
          />
        )
      })}
    </ActionButton>
  )
}

function ImportRow(params: {
  activeIndex: number
  data: DataType
  error?: string[]
  index: number
  onSuccess: () => void
  onError: (value: string[]) => void
}): JSX.Element {
  // Vars
  const { activeIndex, error, index } = params
  const sameIndex: boolean = activeIndex === index
  
  // Hooks
  const api = useApi()
  const masterData = useContext(MasterContext)
  const [data, setData] = useState<DataType>(params.data)

  useEffect(() => {
    if (sameIndex) {
      // Vars
      const defaultSize = masterData.data.size.find(item => item.label === "m3")!
      let error: { [key: string]: string } = {}
      let payload = {
        product_code: masterData.data.product.find(i => i.label.toLowerCase() === data.product_barcode?.toLowerCase())?.product_code,
        product_barcode: data.product_barcode,
        product_name: data.product_name,
        mr_product_group_id: masterData.data.group.find(i => i.label.toLowerCase() === data.mr_product_group?.toLowerCase())?.value ?? "",
        mr_product_style_id: masterData.data.style.find(i => i.label.toLowerCase() === data.mr_product_style?.toLowerCase())?.value ?? "",
        main_size_id: masterData.data.size.find(i => i.label.toLowerCase() === data.main_size?.toLowerCase())?.value ?? defaultSize.value,
        mr_category_customer_id: masterData.data.category.find(i => i.label.toLowerCase() === data.mr_category_customer?.toLowerCase())?.value ?? "",
        mr_product_colour_id: masterData.data.colour.find(i => i.label.toLowerCase() === data.mr_product_colour?.toLowerCase())?.value ?? "",
        mr_product_category_id: masterData.data.product_category.find(i => i.label.toLowerCase() === data.mr_product_category?.toLowerCase())?.value ?? "",
        mr_product_material_id: masterData.data.material.find(i => i.label.toLowerCase() === data.mr_product_material?.toLowerCase())?.value ?? "",
        size_type: data.main_size ? "1" : "2",
        type: "1"
      }
      let param = {
        length: data.size_type === "2" ? data.length : undefined,
        width: data.size_type === "2" ? data.width : undefined,
        height: data.size_type === "2" ? data.height : undefined,
        weight: data.weight,
        weight_unit_id: masterData.data.unit.find(i => i.label.toLowerCase() === data.weight_unit?.toLowerCase())?.value,
        product_id: masterData.data.product.find(i => i.label.toLowerCase() === data.product_barcode?.toLowerCase())?.value ?? undefined
      }

      if (!data.main_size && !defaultSize) {
        params.onError(["Default value on Universal Size is empty"])
        return
      }

      // Function to validate all products
      const validateProducts = async (): Promise<any> => {
        try {
          await productValidationSchema.validate({
            ...payload,
            ...param
          }, { abortEarly: false })
        } catch (err) {
          if (err instanceof ValidationError) {
            // Create an error object
            const errorObject = err.inner.reduce<Record<string, string>>((acc, curr) => {
              if (curr.path) {
                acc[curr.path] = curr.message
              }
              return acc
            }, {})

            return errorObject
          }
        }

        return null
      }

      // Functions
      const getApi = () => {
        if (param.product_id) {
          return api.putForm("/product/edit", payload, { params: param })
        }

        return api.postForm("/product/add", payload, { params: param })
      }
      const generateCode = (): Promise<string> => {
        return new Promise<string>((resolve) => {
          if (payload.product_code) {
            return resolve(payload.product_code)
          }

          api.get(
            "/product/productcode"
          ).then((res: SuccessFetch<{
            payload: { productcode: string }
          }>) => {
            resolve(res.data.payload.productcode)
          }).catch(() => {
            resolve("")
          })
        })
      }
      const saveData = () => {
        getApi().then(() => {
          params.onSuccess()
        }).catch((err: ErrorFetch<{
          message?: string
        }>) => {
          params.onError([err.response.data?.message ?? err.response.message])
        })
      }
      const fetchMasterData = async (value: string[]): Promise<void> => {
        // Vars
        const list = {
          mr_product_group_id: () => fetchMaster.group(
            data.mr_product_group,
            {
              api,
              addNewOption: (value) => masterData.appendData("group", value)
            }
          ).then((res) => {
            payload.mr_product_group_id = res
            delete error["mr_product_group_id"]
          }).catch(err => {
            error["mr_product_group_id"] = err
          }),
          mr_product_style_id: () => fetchMaster.style(
            data.mr_product_style,
            {
              api,
              addNewOption: (value) => masterData.appendData("style", value)
            }
          ).then((res) => {
            payload.mr_product_style_id = res
            delete error["mr_product_style_id"]
          }).catch(err => {
            error["mr_product_style_id"] = err
          }),
          mr_product_colour_id: () => fetchMaster.colour(
            data.mr_product_colour,
            {
              api,
              addNewOption: (value) => masterData.appendData("colour", value)
            }
          ).then((res) => {
            payload.mr_product_colour_id = res
            delete error["mr_product_colour_id"]
          }).catch(err => {
            error["mr_product_colour_id"] = err
          }),
          mr_product_category_id: () => fetchMaster.productCategory(
            data.mr_product_category,
            {
              api,
              addNewOption: (value) => masterData.appendData("product_category", value)
            }
          ).then((res) => {
            payload.mr_product_category_id = res
            delete error["mr_product_category_id"]
          }).catch(err => {
            error["mr_product_category_id"] = err
          }),
          mr_product_material_id: () => fetchMaster.material(
            data.mr_product_material,
            {
              api,
              addNewOption: (value) => masterData.appendData("material", value)
            }
          ).then((res) => {
            payload.mr_product_material_id = res
            delete error["mr_product_material_id"]
          }).catch(err => {
            error["mr_product_material_id"] = err
          }),
          main_size_id: () => fetchMaster.size(
            data.main_size,
            {
              api,
              addNewOption: (value) => masterData.appendData("size", value)
            }
          ).then((res) => {
            payload.main_size_id = res
            delete error["main_size_id"]
          }).catch(err => {
            error["main_size_id"] = err
          }),
          mr_category_customer_id: () => fetchMaster.category(
            data.mr_category_customer,
            {
              api,
              addNewOption: (value) => masterData.appendData("category", value)
            }
          ).then((res) => {
            payload.mr_category_customer_id = res
            delete error["mr_category_customer_id"]
          }).catch(err => {
            error["mr_category_customer_id"] = err
          })
        }

        return await new Promise<void>((resolve, reject) => {
          // Types
          type FieldType = "mr_product_group_id" | "mr_product_style_id" | "mr_product_colour_id" | "mr_product_category_id" | "mr_product_material_id" | "main_size_id" | "mr_category_customer_id"

          return Promise.allSettled(value.map(item => {
            return list[item as FieldType]() || list.main_size_id()
          })).then((res) => {
            if (res.find(item => item.status === "rejected")) {
              return reject()
            }

            return resolve()
          })
        })
      }
      const submitData = async () => {
        // Usage
        generateCode().then(code => {
          if (data.product_code !== code) {
            payload.product_code = code

            setData(prev => {
              return {
                ...prev,
                product_code: code
              }
            })
          }
        }).finally(() => {
          validateProducts().then((errors) => {
            if (errors) {
              // Vars
              const keyList: string[] = Object.keys(errors).filter(item => masterDataKeyList.includes(item))
              error = errors

              fetchMasterData(keyList).then(() => {
                if (JSON.stringify(error) !== '{}') {
                  return params.onError(Object.keys(error).map(item => error[item]))
                }

                saveData()
              }).catch(() => {
                params.onError(Object.keys(error).map(item => error[item]))
              })

              return
            }
    
            saveData()
          })
        })
      }

      submitData()
    }

    // eslint-disable-next-line
  }, [sameIndex])

  return (
    <ImportList
      activeIndex={activeIndex}
      data={data}
      index={index}
      label={`${data.product_barcode} | ${data.product_name}`}
      error={error}
    />
  )
}